import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, UserCheck, Plane, UserCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FlightNotifLevelDialog } from "./FlightNotifLevelDialog";
import { ProfileEditDialog } from "./ProfileEditDialog";

const LEVEL_LABEL = {
  critical: "Kritiskt",
  standard: "Standard",
  all: "Allt",
};
const LEVEL_COLOR = {
  critical: "bg-red-50 text-red-700 border-red-200",
  standard: "bg-emerald-50 text-emerald-700 border-emerald-200",
  all: "bg-blue-50 text-blue-700 border-blue-200",
};

export function FamilySection({ ctx }) {
  const {
    users, currentUser, setNewMemberOpen, setUsers,
    editUserExtraEmails, editUserPhone, toggleChildRole,
  } = ctx;
  const [notifTarget, setNotifTarget] = useState(null);
  const [profileTarget, setProfileTarget] = useState(null);

  const impersonate = async (target) => {
    try {
      // Spara nuvarande admin-token så vi kan gå tillbaka
      const adminToken = localStorage.getItem("grindstugatan_token");
      if (adminToken) localStorage.setItem("grindstugatan_admin_token", adminToken);
      const { data } = await api.post(`/auth/impersonate/${target.id}`);
      localStorage.setItem("grindstugatan_token", data.token);
      toast.success(`Du tittar nu som ${target.name?.split(" ")[0] || target.name}`);
      window.location.href = "/";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte byta vy");
    }
  };

  if (!users || users.length === 0) return null;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display text-base font-medium">Familjemedlemmar</h3>
        {currentUser?.role === "admin" && (
          <Button
            onClick={() => setNewMemberOpen(true)}
            size="sm"
            className="rounded-full bg-[color:var(--moss)] hover:bg-[#1f421f] h-8"
            data-testid="add-member-button"
          >
            <Plus size={14} className="mr-1" /> Ny medlem
          </Button>
        )}
      </div>
      <p className="text-xs text-[color:var(--text-secondary)] mb-2">
        Barn ser bara sina egna Classroom-uppgifter och mejl. Vuxna ser hela familjens data.
      </p>
      <ul className="space-y-2">
        {users
          .filter((u) => !u.email.includes("test_") && !u.email.includes("assignee_"))
          .map((u) => (
          <li key={u.id} className="card-soft p-3" data-testid={`user-${u.id}`}>
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                style={{ backgroundColor: u.avatar_color }}
              >
                {u.name[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{u.name}</p>
                <p className="text-xs text-[color:var(--text-tertiary)] truncate">{u.email}</p>
                {(u.extra_emails || []).length > 0 && (
                  <p className="text-[11px] text-[color:var(--text-secondary)] truncate" title={u.extra_emails.join(", ")}>
                    ✉ {u.extra_emails.join(", ")}
                  </p>
                )}
                {u.phone && (
                  <p className="text-[11px] text-[color:var(--text-secondary)] truncate">📱 {u.phone}</p>
                )}
                {u.is_child && (u.linked_classroom_emails || []).length > 0 && (
                  <p className="text-[10px] text-[color:var(--moss)] mt-0.5 truncate">
                    Länkad till {u.linked_classroom_emails.join(", ")}
                  </p>
                )}
                {u.profile && (
                  <p
                    className="text-[11px] text-[color:var(--text-secondary)] mt-1 italic line-clamp-2"
                    title={u.profile}
                    data-testid={`user-profile-preview-${u.id}`}
                  >
                    “{u.profile}”
                  </p>
                )}
                <p className="text-[10px] text-[color:var(--text-tertiary)] mt-0.5 flex items-center gap-1">
                  <Plane size={10} />
                  <span>Flygnotiser:</span>
                  <span
                    className={`px-1.5 py-px rounded-full border text-[10px] font-medium ${LEVEL_COLOR[u.flight_notif_level || "standard"] || LEVEL_COLOR.standard}`}
                    data-testid={`flight-notif-level-badge-${u.id}`}
                  >
                    {LEVEL_LABEL[u.flight_notif_level || "standard"] || "Standard"}
                  </span>
                </p>
              </div>
              {u.role === "admin" && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)]">
                  Admin
                </span>
              )}
              {u.is_child && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F0F6E6] text-[color:var(--moss)]">
                  Barn
                </span>
              )}
              {currentUser?.role === "admin" && (
                <div className="flex flex-col items-end gap-0.5">
                  <button
                    onClick={() => editUserExtraEmails(u)}
                    className="text-[11px] font-medium text-[color:var(--jarvis)] hover:underline px-1"
                    data-testid={`user-edit-extra-emails-${u.id}`}
                    title="Lägg till extra e-postadresser (alias)"
                  >
                    {(u.extra_emails || []).length > 0 ? "Ändra mail" : "+ Mail"}
                  </button>
                  <button
                    onClick={() => editUserPhone(u)}
                    className="text-[11px] font-medium text-[color:var(--jarvis)] hover:underline px-1"
                    data-testid={`user-edit-phone-${u.id}`}
                    title={u.phone ? "Ändra mobilnummer" : "Lägg till mobilnummer"}
                  >
                    {u.phone ? "Ändra mobil" : "+ Mobil"}
                  </button>
                  <button
                    onClick={() => setProfileTarget(u)}
                    className="text-[11px] font-medium text-[color:var(--jarvis)] hover:underline px-1 inline-flex items-center gap-1"
                    data-testid={`user-edit-profile-${u.id}`}
                    title="Redigera profil (Edit-kontext)"
                  >
                    <UserCircle2 size={11} /> {u.profile ? "Ändra profil" : "+ Profil"}
                  </button>
                  <button
                    onClick={() => setNotifTarget(u)}
                    className="text-[11px] font-medium text-[color:var(--jarvis)] hover:underline px-1 inline-flex items-center gap-1"
                    data-testid={`user-edit-flight-notif-level-${u.id}`}
                    title="Välj nivå för flygnotiser"
                  >
                    <Plane size={11} /> Flygnotiser
                  </button>
                  {u.id !== currentUser?.id && (
                    <button
                      onClick={() => toggleChildRole(u)}
                      className="text-[11px] font-medium text-[color:var(--jarvis)] hover:underline px-1"
                      data-testid={`user-toggle-child-${u.id}`}
                      title={u.is_child ? "Markera som vuxen (ser all data)" : "Markera som barn (ser bara sin egen data)"}
                    >
                      {u.is_child ? "Gör vuxen" : "Gör barn"}
                    </button>
                  )}
                  {u.is_child && u.id !== currentUser?.id && !currentUser?.is_child && (
                    <button
                      onClick={() => impersonate(u)}
                      className="text-[11px] font-medium text-[color:var(--moss)] hover:underline px-1 inline-flex items-center gap-1"
                      data-testid={`impersonate-${u.id}`}
                      title={`Se appen som ${u.name?.split(' ')[0] || u.name}`}
                    >
                      <UserCheck size={11} /> Logga in som {u.name?.split(' ')[0] || u.name}
                    </button>
                  )}
                </div>
              )}
              {currentUser?.role !== "admin" && u.id === currentUser?.id && (
                <div className="flex flex-col items-end gap-0.5">
                  <button
                    onClick={() => setProfileTarget(u)}
                    className="text-[11px] font-medium text-[color:var(--jarvis)] hover:underline px-2 inline-flex items-center gap-1"
                    data-testid={`user-edit-profile-${u.id}`}
                    title="Redigera profil (Edit-kontext)"
                  >
                    <UserCircle2 size={11} /> {u.profile ? "Profil" : "+ Profil"}
                  </button>
                  <button
                    onClick={() => setNotifTarget(u)}
                    className="text-[11px] font-medium text-[color:var(--jarvis)] hover:underline px-2 inline-flex items-center gap-1"
                    data-testid={`user-edit-flight-notif-level-${u.id}`}
                    title="Välj nivå för flygnotiser"
                  >
                    <Plane size={11} /> Flygnotiser
                  </button>
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
      <FlightNotifLevelDialog
        open={!!notifTarget}
        onOpenChange={(o) => !o && setNotifTarget(null)}
        targetUser={notifTarget}
        onSaved={(newLevel) => {
          if (!notifTarget) return;
          if (typeof setUsers === "function") {
            setUsers((prev) => prev.map((x) =>
              x.id === notifTarget.id
                ? { ...x, flight_notif_level: newLevel }
                : x,
            ));
          }
        }}
      />
      <ProfileEditDialog
        open={!!profileTarget}
        onOpenChange={(o) => !o && setProfileTarget(null)}
        targetUser={profileTarget}
        onSaved={(newProfile) => {
          if (!profileTarget) return;
          if (typeof setUsers === "function") {
            setUsers((prev) => prev.map((x) =>
              x.id === profileTarget.id
                ? { ...x, profile: newProfile || null }
                : x,
            ));
          }
        }}
      />
    </div>
  );
}
